import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { redirectAfterAuth, hasPendingReviewReturn } from "@/lib/authRedirect";

/**
 * Google Identity Services `Sign in with Google` button.
 *
 * Uses the OFFICIAL rendered button (google.accounts.id.renderButton) with
 * popup UX — no redirect, no `#session_id` hash roundtrip. On success, GIS
 * hands us a signed ID token which we POST to /api/auth/google; the backend
 * verifies signature/audience/issuer/expiry with Google's public keys and
 * mints a TSR session cookie identical to the old flow.
 *
 * Props:
 *   - text: "signin_with" | "signup_with" (default "signin_with")
 *   - fallbackTo: path to navigate on success when no return-to is saved
 */
export default function GoogleSignInButton({ text = "signin_with", fallbackTo = "/profile", testid = "google-signin-btn" }) {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const holderRef = useRef(null);
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;

    async function handleCredential(response) {
      if (!response?.credential) return;
      try {
        const r = await api.post("/auth/google", { credential: response.credential });
        setUser(r.data.user);
        localStorage.setItem("k12_user", JSON.stringify(r.data.user));
        // Post-auth routing priority: pending review > onboarding > fallback.
        // A guest mid-review who signs up via Google should complete the
        // review submission first; onboarding surfaces on next normal entry.
        if (hasPendingReviewReturn()) {
          redirectAfterAuth(nav, fallbackTo);
        } else if (r.data.user && !r.data.user.onboarded) {
          nav("/onboarding", { replace: true });
        } else {
          redirectAfterAuth(nav, fallbackTo);
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Login Google gagal. Coba lagi.");
      }
    }

    function tryInit() {
      if (cancelled) return;
      const g = window.google?.accounts?.id;
      if (!g || !holderRef.current) {
        // GIS script still loading — retry shortly.
        setTimeout(tryInit, 120);
        return;
      }
      g.initialize({
        client_id: clientId,
        callback: handleCredential,
        ux_mode: "popup",
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      // Render an official button that matches Google's brand guidelines.
      g.renderButton(holderRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        text,          // "signin_with" | "signup_with"
        shape: "pill",
        logo_alignment: "left",
        width: holderRef.current.offsetWidth || 320,
      });
    }
    tryInit();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, text]);

  if (!clientId) {
    return (
      <div data-testid={`${testid}-missing-config`} className="text-xs text-[#B91C1C] p-3 rounded-lg bg-[#FEF2F2] border border-[#FECACA]">
        Google login belum dikonfigurasi (REACT_APP_GOOGLE_CLIENT_ID kosong).
      </div>
    );
  }
  return <div ref={holderRef} data-testid={testid} className="w-full flex justify-center min-h-[44px]" />;
}
